'use client';

import type { ApiErrorType, NewsletterProps } from '@/core/types';
import type { NewsletterSchemaType } from './schema/newsletter-schema';

import React from 'react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslate } from '@/core/locales';
import { processApiError } from '@/core/utils';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

// import { subscribe } from '@/actions/newsletter';
import { Form, Field } from '@/components/hook-form';
import { Box, Button, Container, Typography } from '@/components/ui';

import { newsletterSchema } from './schema/newsletter-schema';

export default function Newsletter({ title, subTitle, className = '' }: NewsletterProps) {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);

  const NewsletterSchema = newsletterSchema(t);

  const defaultValues: NewsletterSchemaType = {
    email: '',
  };

  const methods = useForm<NewsletterSchemaType>({
    resolver: zodResolver(NewsletterSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      // await subscribe(data);
      await new Promise((resolve) => setTimeout(resolve, 500));
      reset();
      toast.success(t('forms.newsletter.success'));
    } catch (error) {
      processApiError(error as ApiErrorType, setError, t, t('forms.newsletter.error'));
    }
  });

  return (
    <div className={`relative w-full bg-light-gray/56 ${className}`}>
      <Container className="flex flex-col items-center justify-center text-center py-[38px] md:py-[90px]">
        <Box className="w-full max-w-[970px]">
          {title && (
            <Typography
              variant="h2"
              className="text-2xl text-center md:text-4xl lg:leading-[61px] font-bold mb-6 uppercase"
            >
              {title}
            </Typography>
          )}
          {subTitle && (
            <Typography
              variant="body1"
              paragraph
              className="hidden text-center md:block text-lg  md:text-xl mb-8 mb-[48px]"
            >
              {subTitle}
            </Typography>
          )}
        </Box>
        <Form methods={methods} onSubmit={onSubmit} className="w-full max-w-md">
          <Box className="flex items-start gap-3 max-w-[550px] mx-auto">
            <Field.Text
              name="email"
              placeholder={t('fields.placeholder.email')}
              className="flex-grow"
            />
            <Button
              type="submit"
              variant="contained"
              loading={isSubmitting}
              loadingText={t('actions.subscribing')}
            >
              {t('actions.subscribe')}
            </Button>
          </Box>
        </Form>
      </Container>
    </div>
  );
}
